import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import { API_URL } from '../config';
import './ManageCustomersPage.css';

const customersApiUrl = `${API_URL}/Customers`;

const ManageCustomersPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerForm, setOfferForm] = useState({
    subject: '',
    offerDetails: ''
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Admin') {
      navigate('/');
      return;
    }
    fetchCustomers();
  }, [isLoggedIn, userRole, navigate]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(customersApiUrl, { headers: getAuthHeader() });
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.$values ?? []);
      setCustomers(list);
    } catch (err) {
      const errorMessage = err.response?.status === 401
        ? 'Unauthorized. Please log in again.'
        : err.response?.status === 403
          ? 'Access denied. Admin role required.'
          : err.response?.data?.message || err.message || 'Failed to load customers';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(
    (c) =>
      (c.customerCode && c.customerCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.username && c.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.mobile && c.mobile.includes(searchTerm)) ||
      (c.alternateMobile && c.alternateMobile.includes(searchTerm)) ||
      (c.city && c.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.state && c.state.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.pinCode && c.pinCode.includes(searchTerm)) ||
      (c.referredByCode && c.referredByCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.referredByName && c.referredByName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleToggleStatus = async (customer) => {
    setStatusLoadingId(customer.userId);
    setError(null);
    try {
      await axios.put(
        `${customersApiUrl}/${customer.userId}/status`,
        { isActive: !customer.isActive },
        { headers: getAuthHeader() }
      );
      setCustomers((prev) =>
        prev.map((item) =>
          item.userId === customer.userId ? { ...item, isActive: !item.isActive } : item
        )
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update customer status.');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const openOfferModal = (customer) => {
    setSelectedCustomer(customer);
    setOfferForm({
      subject: 'Special Offer from Yelooo',
      offerDetails: `Hi ${customer.name || customer.username},

We are excited to share a special offer with you.

Offer details:

Thank you,
Yelooo`
    });
    setOfferModalOpen(true);
    setError(null);
  };

  const closeOfferModal = () => {
    setOfferModalOpen(false);
    setSelectedCustomer(null);
    setOfferLoading(false);
  };

  const handleOfferChange = (e) => {
    const { name, value } = e.target;
    setOfferForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOfferEmail = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    setOfferLoading(true);
    setError(null);
    try {
      await axios.post(
        `${customersApiUrl}/${selectedCustomer.userId}/send-offer-email`,
        {
          subject: offerForm.subject,
          offerDetails: offerForm.offerDetails,
        },
        { headers: getAuthHeader() }
      );
      closeOfferModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send offer email.');
      setOfferLoading(false);
    }
  };

  return (
    <div className="manage-customers-wrapper">
      <Header />
      <div className="manage-customers-container">
        <div className="page-header">
          <h1>Customer List</h1>
        </div>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by code, name, email, mobile, city, state, pin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="manage-customers-loading">Loading customers...</div>
        ) : error ? (
          <div className="manage-customers-error">{error}</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="manage-customers-empty">
            {searchTerm ? 'No customers match your search.' : 'No customers found.'}
          </div>
        ) : (
          <div className="customers-table-container">
            <table className="customers-table">
              <thead>
                <tr>
                  <th>Customer Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>KYC</th>
                  <th>Referred By</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Pin</th>
                  <th>Joined</th>
                  <th>T&amp;C</th>
                  <th>Address</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.userId}>
                    <td>{customer.customerCode || customer.userId}</td>
                    <td>{customer.name || '—'}</td>
                    <td>{customer.email || '—'}</td>
                    <td>{customer.mobile || customer.alternateMobile || '—'}</td>
                    <td>
                      <span className={`customer-status-badge ${customer.isActive ? 'active' : 'inactive'}`}>
                        {customer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{customer.kycStatus || 'NotSubmitted'}</td>
                    <td>
                      {customer.referredByCode || customer.referredByName
                        ? `${customer.referredByCode || ''}${customer.referredByCode && customer.referredByName ? ' - ' : ''}${customer.referredByName || ''}`
                        : 'Direct'}
                    </td>
                    <td>{customer.city || '—'}</td>
                    <td>{customer.state || '—'}</td>
                    <td>{customer.pinCode || '—'}</td>
                    <td>{formatDate(customer.createdAt)}</td>
                    <td>
                      <span className={`customer-status-badge ${customer.termsAccepted ? 'active' : 'inactive'}`}>
                        {customer.termsAccepted ? 'Accepted' : '—'}
                      </span>
                    </td>
                    <td className="address-cell">{customer.address || '—'}</td>
                    <td>
                      <div className="customer-actions">
                        <button
                          type="button"
                          className={`customer-status-btn ${customer.isActive ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleStatus(customer)}
                          disabled={statusLoadingId === customer.userId}
                        >
                          {statusLoadingId === customer.userId
                            ? 'Saving...'
                            : customer.isActive
                              ? 'Mark Inactive'
                              : 'Mark Active'}
                        </button>
                        <button
                          type="button"
                          className="customer-offer-btn"
                          onClick={() => openOfferModal(customer)}
                        >
                          Send Offer Email
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="total-count">Total: {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}</div>
      </div>

      {offerModalOpen && selectedCustomer && (
        <div className="customer-offer-modal-overlay" onClick={closeOfferModal}>
          <div className="customer-offer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="customer-offer-modal-header">
              <h2>Send Offer Email</h2>
              <button type="button" className="customer-offer-close" onClick={closeOfferModal}>×</button>
            </div>
            <p className="customer-offer-recipient">
              To: <strong>{selectedCustomer.email}</strong>
            </p>
            <form onSubmit={handleSendOfferEmail} className="customer-offer-form">
              <label>
                Subject
                <input
                  type="text"
                  name="subject"
                  value={offerForm.subject}
                  onChange={handleOfferChange}
                  disabled={offerLoading}
                  required
                />
              </label>
              <label>
                Offer Details
                <textarea
                  name="offerDetails"
                  value={offerForm.offerDetails}
                  onChange={handleOfferChange}
                  rows={10}
                  disabled={offerLoading}
                  required
                />
              </label>
              <div className="customer-offer-modal-actions">
                <button type="button" className="customer-offer-cancel" onClick={closeOfferModal} disabled={offerLoading}>
                  Cancel
                </button>
                <button type="submit" className="customer-offer-send" disabled={offerLoading}>
                  {offerLoading ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCustomersPage;
